import { create } from 'zustand';
import { Person, Machine, Material, Product, Workflow } from '@/types';
import { HistoryEntry } from '@/types/history';

interface ResourceState {
  persons: Person[];
  machines: Machine[];
  materials: Material[];
  products: Product[];
  workflows: Workflow[];
  personHistory: HistoryEntry[];  // 人員變更歷史 | Person change history

  // Actions
  setPersons: (persons: Person[]) => void;
  setMachines: (machines: Machine[]) => void;
  setMaterials: (materials: Material[]) => void;
  setProducts: (products: Product[]) => void;
  setWorkflows: (workflows: Workflow[]) => void;

  // Getters
  getPersonById: (id: string) => Person | undefined;
  getMachineById: (id: string) => Machine | undefined;
  getMaterialById: (id: string) => Material | undefined;
  getProductById: (id: string) => Product | undefined;
  getWorkflowById: (id: string) => Workflow | undefined;

  // Utility getters
  getAvailablePersons: () => Person[];
  getAvailableMachines: () => Machine[];
  getLowStockMaterials: () => Material[];

  // Person management | 人員管理
  updatePerson: (id: string, updates: Partial<Person>, reason?: string) => void;
  deletePerson: (id: string, reason?: string) => void;
  createPerson: (person: Person) => void;

  // History management | 歷史管理
  setPersonHistory: (history: HistoryEntry[]) => void;
  addPersonHistory: (entry: HistoryEntry) => void;
  getPersonHistory: (entityId?: string) => HistoryEntry[];
  clearPersonHistory: () => void;
}

export const useResourceStore = create<ResourceState>((set, get) => ({
  // Initial state
  persons: [],
  machines: [],
  materials: [],
  products: [],
  workflows: [],
  personHistory: [],

  // Setters
  setPersons: (persons) => set({ persons }),
  setMachines: (machines) => set({ machines }),
  setMaterials: (materials) => set({ materials }),
  setProducts: (products) => set({ products }),
  setWorkflows: (workflows) => set({ workflows }),
  setPersonHistory: (personHistory) => set({ personHistory }),

  // Getters
  getPersonById: (id) => get().persons.find((person) => person.id === id),
  getMachineById: (id) => get().machines.find((machine) => machine.id === id),
  getMaterialById: (id) => get().materials.find((material) => material.id === id),
  getProductById: (id) => get().products.find((product) => product.id === id),
  getWorkflowById: (id) => get().workflows.find((workflow) => workflow.id === id),

  // Utility getters
  getAvailablePersons: () =>
    get().persons.filter((person) => person.status === '啟用'),

  getAvailableMachines: () =>
    get().machines.filter(
      (machine) => machine.status === '啟用' && machine.maintenanceStatus === '正常'
    ),

  getLowStockMaterials: () =>
    get().materials.filter(
      (material) => material.stockQuantity <= material.safetyStock
    ),

  // Person management methods | 人員管理方法

  // 更新人員資料 | Update person data
  updatePerson: (id, updates, reason) => {
    const { persons } = get();
    const personIndex = persons.findIndex((p) => p.id === id);

    if (personIndex === -1) {
      console.warn(`人員 ID ${id} 不存在 | Person ID ${id} not found`);
      return;
    }

    const oldPerson = persons[personIndex];
    const newPerson = { ...oldPerson, ...updates };

    // 建立歷史記錄 | Create history record
    const historyEntry: HistoryEntry = {
      id: `hist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      action: 'update',
      entityType: 'person',
      entityId: id,
      entityName: oldPerson.name,
      changes: Object.keys(updates).map((key) => ({
        field: key,
        fieldName: getFieldDisplayName(key),
        oldValue: oldPerson[key as keyof Person],
        newValue: updates[key as keyof Person],
      })),
      reason,
    };

    // 更新人員列表 | Update persons list
    const updatedPersons = [...persons];
    updatedPersons[personIndex] = newPerson;

    // 更新歷史 | Update history
    const updatedHistory = [...get().personHistory, historyEntry];

    set({
      persons: updatedPersons,
      personHistory: updatedHistory,
    });

    console.log('人員已更新 | Person updated:', id, updates);
  },

  // 刪除人員 | Delete person
  deletePerson: (id, reason) => {
    const { persons } = get();
    const person = persons.find((p) => p.id === id);

    if (!person) {
      console.warn(`人員 ID ${id} 不存在 | Person ID ${id} not found`);
      return;
    }

    // 建立歷史記錄 | Create history record
    const historyEntry: HistoryEntry = {
      id: `hist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      action: 'delete',
      entityType: 'person',
      entityId: id,
      entityName: person.name,
      changes: [{
        field: 'all',
        fieldName: '全部',
        oldValue: person,
        newValue: null,
      }],
      reason,
    };

    // 更新人員列表 | Update persons list
    const updatedPersons = persons.filter((p) => p.id !== id);

    // 更新歷史 | Update history
    const updatedHistory = [...get().personHistory, historyEntry];

    set({
      persons: updatedPersons,
      personHistory: updatedHistory,
    });

    console.log('人員已刪除 | Person deleted:', id);
  },

  // 建立新人員 | Create new person
  createPerson: (person) => {
    const { persons } = get();

    // 檢查 ID 是否已存在 | Check if ID already exists
    if (persons.some((p) => p.id === person.id)) {
      console.warn(`人員 ID ${person.id} 已存在 | Person ID ${person.id} already exists`);
      return;
    }

    // 建立歷史記錄 | Create history record
    const historyEntry: HistoryEntry = {
      id: `hist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      action: 'create',
      entityType: 'person',
      entityId: person.id,
      entityName: person.name,
      changes: [{
        field: 'all',
        fieldName: '全部',
        oldValue: null,
        newValue: person,
      }],
    };

    // 更新人員列表 | Update persons list
    const updatedPersons = [...persons, person];

    // 更新歷史 | Update history
    const updatedHistory = [...get().personHistory, historyEntry];

    set({
      persons: updatedPersons,
      personHistory: updatedHistory,
    });

    console.log('新人員已建立 | New person created:', person.id);
  },

  // History management methods | 歷史管理方法

  // 新增歷史記錄 | Add history entry
  addPersonHistory: (entry) => {
    const updatedHistory = [...get().personHistory, entry];
    set({ personHistory: updatedHistory });
  },

  // 獲取歷史記錄 | Get history records
  getPersonHistory: (entityId) => {
    const { personHistory } = get();
    if (entityId) {
      return personHistory.filter((entry) => entry.entityId === entityId);
    }
    return personHistory;
  },

  // 清除歷史記錄 | Clear history records
  clearPersonHistory: () => {
    set({ personHistory: [] });
    console.log('歷史記錄已清除 | History cleared');
  },
}));

/**
 * 獲取欄位顯示名稱 | Get field display name
 */
function getFieldDisplayName(field: string): string {
  const fieldNames: Record<string, string> = {
    id: '工號',
    name: '姓名',
    email: '電子郵件',
    plants: '廠別',
    department: '部門',
    roles: '角色',
    shiftType: '班別',
    status: '狀態',
    dailyHours: '每日工時',
    skills: '技能',
    hireDate: '僱用日期',
  };
  return fieldNames[field] || field;
}
